import type { NormalizedConversion } from '../types';

type FetchFn = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

export interface MetaCapiClientOptions {
  readonly pixelId: string;
  readonly endpoint?: string;
  readonly fetchFn?: FetchFn;
  readonly onError?: (error: unknown) => void;
}

/**
 * Host context required on every CAPI POST. `tenantId` is how the hen-site
 * relay authorizes `pixel_id`. `consent` is always `'granted'` because this
 * client must not POST otherwise; marketing consent is still the caller's job
 * (`usePaidSocialEmitter` on hen-site).
 */
export interface MetaCapiSendOptions {
  readonly tenantId: string;
}

interface MetaCapiPayload {
  readonly pixel_id: string;
  readonly event_name: NormalizedConversion['name'];
  readonly event_id: string;
  readonly event_time: number;
  readonly event_source_url: string;
  readonly tenantId: string;
  readonly consent: 'granted';
  readonly fbc?: string;
  readonly fbp?: string;
}

const readCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const prefix = `${name}=`;
  const match = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  if (!match) {
    return undefined;
  }

  try {
    return decodeURIComponent(match.slice(prefix.length));
  } catch {
    return undefined;
  }
};

const buildFbc = (pageUrl: string, timestamp: number): string | undefined => {
  try {
    const fbclid = new URL(pageUrl).searchParams.get('fbclid')?.trim();
    return fbclid
      ? `fb.1.${Math.floor(timestamp / 1_000)}.${fbclid}`
      : undefined;
  } catch {
    return undefined;
  }
};

export class MetaCapiClient {
  private readonly pixelId: string;
  private readonly endpoint: string;
  private readonly fetchFn: FetchFn;
  private readonly onError?: (error: unknown) => void;

  constructor({
    pixelId,
    endpoint = '/api/hen/v1/meta/capi',
    fetchFn = (input, init) => fetch(input, init),
    onError,
  }: MetaCapiClientOptions) {
    this.pixelId = pixelId;
    this.endpoint = endpoint;
    this.fetchFn = fetchFn;
    this.onError = onError;
  }

  send(conversion: NormalizedConversion, options: MetaCapiSendOptions): void {
    const tenantId = options.tenantId.trim();
    if (!tenantId) {
      this.onError?.(new Error('MetaCapiTenantIdMissing'));
      return;
    }

    const fbc =
      readCookie('_fbc') ?? buildFbc(conversion.pageUrl, conversion.timestamp);
    const fbp = readCookie('_fbp');
    const payload: MetaCapiPayload = {
      pixel_id: this.pixelId,
      event_name: conversion.name,
      event_id: conversion.eventId,
      event_time: Math.floor(conversion.timestamp / 1_000),
      event_source_url: conversion.pageUrl,
      tenantId,
      consent: 'granted',
      ...(fbc ? { fbc } : {}),
      ...(fbp ? { fbp } : {}),
    };

    try {
      void this.fetchFn(this.endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      })
        .then((response) => {
          if (!response.ok) {
            this.onError?.(new Error('MetaCapiRequestFailed'));
          }
        })
        .catch((error: unknown) => this.onError?.(error));
    } catch (error) {
      this.onError?.(error);
    }
  }
}
